import { Button, Typography, Box, Alert, CircularProgress } from '@mui/material'
import { CheckCircleOutline as CheckCircleIcon } from '@mui/icons-material'
import DialogShell from '../common/DialogShell'
import { JobOpportunity } from '../../services/api'
import { APPLY_DECISION_LABELS } from '../../state/jobOpportunities'

interface ConfirmSubmissionDialogProps {
  open: boolean
  job: JobOpportunity | null
  submitting?: boolean
  onConfirm: () => void
  onCancel: () => void
}

/**
 * MWA-224 — confirmation gate for moving a job application from the protected
 * "Applying" stage to "Applied". Staff must confirm they actually submitted the
 * application; that confirmation is what the parent persists (submissionConfirmed:true).
 * If the client hasn't recorded a go-ahead decision yet, the move is blocked here
 * (and again server-side) so a card can't slip into "Applied" prematurely.
 */
export default function ConfirmSubmissionDialog({
  open,
  job,
  submitting = false,
  onConfirm,
  onCancel,
}: ConfirmSubmissionDialogProps) {
  if (!job) return null

  const decision = job.applyDecision
  const hasGoAhead = decision === 'apply_on_behalf' || decision === 'self_apply'

  return (
    <DialogShell
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      compact
      title="Move to “Applied”?"
      subtitle={`${job.company} — ${job.jobTitle}`}
      footer={
        <>
          <Button onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={onConfirm} variant="contained" disabled={!hasGoAhead || submitting}>
            {submitting ? <CircularProgress size={20} /> : 'Yes, submitted'}
          </Button>
        </>
      }
    >
      {hasGoAhead ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              p: 1.5,
              border: 1,
              borderColor: 'success.main',
              borderRadius: '8px',
            }}
          >
            <CheckCircleIcon fontSize="small" sx={{ color: 'success.dark', mt: '1px' }} />
            <Typography variant="body2" component="div">
              Client decision: <strong>{APPLY_DECISION_LABELS[decision] || decision}</strong>
            </Typography>
          </Box>
          <Typography variant="body1">
            Have you submitted this application to the employer?
          </Typography>
        </Box>
      ) : (
        <Alert severity="warning" variant="outlined">
          This application can’t move to <strong>Applied</strong> yet — the client
          hasn’t approved it. Ask the client to record their decision first
          (Apply on my behalf / I’ll apply myself).
        </Alert>
      )}
    </DialogShell>
  )
}
